import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { auth, db, rtdb } from '../config/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import {
  doc,
  getDoc,
  setDoc,
  onSnapshot,
  serverTimestamp,
} from 'firebase/firestore';
import { ref, set, update as rtdbUpdate, serverTimestamp as rtdbServerTimestamp, onDisconnect } from 'firebase/database';
import { FIRESTORE_PATHS, RTDB_SEGMENTS, SUBSCRIPTION_CONSTANTS, APP_CONFIG } from '../config/appConfig';
import logger from '../utils/logger';

const UserContext = createContext();

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [userSubscription, setUserSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  // Reference for heartbeat interval cleanup
  const heartbeatIntervalRef = useRef(null);
  // Reference for subscription snapshot listener cleanup
  const subscriptionUnsubRef = useRef(null);

  /**
   * ============================================================================
   * SUBSCRIPTION MANAGEMENT LOGIC
   * ============================================================================
   */

  /**
   * Fetches the user's active subscription from Firestore.
   * Path: Subscriptions/{userId}
   */
  const fetchUserSubscription = async (userId) => {
    try {
      logger.debug(`[Subscription] Fetching for user: ${userId}`);

      // 1. Check Firestore 'Subscriptions' collection with User ID as Key
      // Path: Subscriptions/{userId}
      const subscriptionRef = doc(db, FIRESTORE_PATHS.SUBSCRIPTIONS, userId);
      const subscriptionDoc = await getDoc(subscriptionRef);

      if (subscriptionDoc.exists()) {
        const subData = subscriptionDoc.data();
        logger.debug(`[Subscription] Found in Firestore:`, subData);
        return {
          id: subscriptionDoc.id,
          ...subData
        };
      }

      logger.info('Subscription', 'No subscription found in Firestore.');
      return null;

    } catch (error) {
      logger.error('Subscription', 'Error fetching:', error);
      return null;
    }
  };

  /**
   * Creates or updates a subscription record in Firestore.
   * Uses User ID as document key. Path: Subscriptions/{userId}
   */
  const createOrUpdateSubscription = async (userId, subscriptionPlan, subscriptionStatus, additionalData = {}) => {
    try {
      logger.debug(`[Subscription] Updating for ${userId}: ${subscriptionPlan} (${subscriptionStatus})`);

      // Timestamps
      const timestamp = new Date().toISOString();
      const firestoreTimestamp = serverTimestamp();

      // Determine billing cycle from plan ID and parse base plan for database
      const basePlan = SUBSCRIPTION_CONSTANTS.getDbPlan(subscriptionPlan);
      const billingCycle = (additionalData?.billingCycle) || (subscriptionPlan.toLowerCase().includes('yearly') ? 'yearly' : 'monthly');

      const subscriptionData = {
        userId,
        subscriptionPlan: basePlan,
        subscriptionStatus,
        billingCycle,
        updatedAt: firestoreTimestamp,
        ...additionalData
      };

      /**
       * FIRESTORE WRITE — Client-side writes are restricted by Firestore security rules:
       * - Create: only allowed for 'basic' plan (new user default)
       * - Update: only allowed for 'basic' plan with 'inactive' or 'cancelled' status
       * All paid subscription updates go through Cloud Functions (admin SDK).
       * Path: Subscriptions/{userId}
       */
      const subscriptionRef = doc(db, FIRESTORE_PATHS.SUBSCRIPTIONS, userId);

      // Check existence first to determine if we need to set createdAt
      const snap = await getDoc(subscriptionRef);
      if (!snap.exists()) {
        // New Record
        await setDoc(subscriptionRef, {
          ...subscriptionData,
          createdAt: firestoreTimestamp
        });
        logger.debug(`[Subscription] Firestore Created (Key: ${userId})`);
      } else {
        // Update Existing
        await setDoc(subscriptionRef, subscriptionData, { merge: true });
        logger.debug(`[Subscription] Firestore Updated (Key: ${userId})`);
      }

      // Return the final state
      return {
        id: userId,
        subscriptionPlan,
        subscriptionStatus,
        billingCycle,
        updatedAt: timestamp,
        ...additionalData
      };

    } catch (error) {
      logger.error('Subscription', 'CRITICAL ERROR updating subscription:', error);
      throw error;
    }
  };

  /**
   * ============================================================================
   * USER PROFILE & AUTH
   * ============================================================================
   * Profile is stored directly on Users/{userId} document (consolidated).
   * No more dual-write to legacy 'users' collection or PlayerProfile subcollection.
   */
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      // Clean up previous heartbeat
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }

      if (firebaseUser) {
        setUser(firebaseUser);

        // --- HEARTBEAT LOGIC (RTDB Presence) ---
        // Writes presence to RTDB DefaultDatabase/{uid}/presence (Firebase recommended pattern).
        // Interval: 60s — DugoutModule considers users "online" if lastActive < 5 min ago.
        // onDisconnect handler automatically marks user offline when browser closes.
        const presenceRef = ref(rtdb, `DefaultDatabase/${firebaseUser.uid}/presence`);
        const updateHeartbeat = async () => {
          try {
            await set(presenceRef, {
              lastActive: rtdbServerTimestamp(),
              status: 'online',
              source: 'cricketxtreme'
            });
          } catch (err) {
            logger.error('Heartbeat', 'Error:', err);
          }
        };

        // Set up onDisconnect to mark offline when browser closes
        onDisconnect(presenceRef).set({
          lastActive: rtdbServerTimestamp(),
          status: 'offline',
          source: 'cricketxtreme'
        });

        updateHeartbeat();
        heartbeatIntervalRef.current = setInterval(updateHeartbeat, 60000);

        // Write build version to RTDB (so all apps can see each other's versions).
        // Uses update() to merge — won't overwrite other apps' version keys.
        const buildInfoRef = ref(rtdb, `${RTDB_SEGMENTS.USER_ROOT}/${firebaseUser.uid}/build`);
        rtdbUpdate(buildInfoRef, {
          AppVersionCricketXtreme: APP_CONFIG.version || '1.0.0'
        }).catch(err => logger.error('Build', 'Error writing version:', err));
        // -----------------------

        const loadUserData = async () => {
          try {
            // 1. Load User Profile directly from Users/{userId}
            // Consolidated: profile fields live on the user document itself
            const profileRef = doc(db, FIRESTORE_PATHS.USERS, firebaseUser.uid);
            const profileDoc = await getDoc(profileRef);

            let profileData = null;

            if (profileDoc.exists()) {
              profileData = profileDoc.data();
            } else {
              // Initialize new user profile directly on Users/{userId}
              logger.info('Auth', 'Initializing new user profile...');
              const defaultProfile = {
                email: firebaseUser.email,
                displayName: firebaseUser.displayName || APP_CONFIG.defaultUserName,
                photoURL: firebaseUser.photoURL || null,
                createdAt: new Date().toISOString(),
                onboardingComplete: false
              };
              await setDoc(profileRef, defaultProfile, { merge: true });
              profileData = defaultProfile;
            }

            // 2. Load Subscription (initial fetch)
            let subscription = await fetchUserSubscription(firebaseUser.uid);

            // If no subscription, create default (Basic/Inactive)
            if (!subscription) {
              logger.info('Auth', 'New user detected, assigning default subscription.');
              subscription = await createOrUpdateSubscription(
                firebaseUser.uid,
                SUBSCRIPTION_CONSTANTS.DEFAULT_PLAN,
                SUBSCRIPTION_CONSTANTS.DEFAULT_STATUS
              );

              // Auto-complete onboarding for free users
              if (!profileData.onboardingComplete) {
                await setDoc(profileRef, { onboardingComplete: true }, { merge: true });
                profileData.onboardingComplete = true;
              }
            }

            setUserProfile(profileData);
            setUserSubscription(subscription);

            // 3. Set up real-time listener for subscription changes.
            // This ensures changes made on OTHER apps (CricketX-Website, webhooks)
            // are reflected in real-time without needing to refresh the page.
            if (subscriptionUnsubRef.current) subscriptionUnsubRef.current();
            const subRef = doc(db, FIRESTORE_PATHS.SUBSCRIPTIONS, firebaseUser.uid);
            subscriptionUnsubRef.current = onSnapshot(subRef, (snap) => {
              if (snap.exists()) {
                const subData = { id: snap.id, ...snap.data() };
                logger.debug('[Subscription] Real-time update:', subData.subscriptionPlan, subData.subscriptionStatus);
                setUserSubscription(subData);
              }
            }, (err) => {
              logger.error('Subscription', 'Snapshot listener error:', err);
            });

          } catch (error) {
            logger.error('Auth', 'Error loading user data:', error);
          }
        };

        loadUserData();
      } else {
        // Logged out — clean up subscription listener
        if (subscriptionUnsubRef.current) {
          subscriptionUnsubRef.current();
          subscriptionUnsubRef.current = null;
        }
        setUser(null);
        setUserProfile(null);
        setUserSubscription(null);
      }
      setLoading(false);
    });

    return () => {
      unsubscribe();
      if (heartbeatIntervalRef.current) clearInterval(heartbeatIntervalRef.current);
      if (subscriptionUnsubRef.current) subscriptionUnsubRef.current();
    };
  }, []);

  // Update Profile — writes to Users/{userId} only (consolidated, no dual-write)
  const updateUserProfile = async (updates) => {
    if (!user) return;
    try {
      const profileRef = doc(db, FIRESTORE_PATHS.USERS, user.uid);
      await setDoc(profileRef, updates, { merge: true });
      setUserProfile(prev => ({ ...prev, ...updates }));
    } catch (error) {
      logger.error('Profile', 'Error updating:', error);
      throw error;
    }
  };

  // Update Subscription wrapper
  const updateSubscription = async (subscriptionPlan, subscriptionStatus, additionalData = {}) => {
    if (!user) return;
    try {
      const result = await createOrUpdateSubscription(user.uid, subscriptionPlan, subscriptionStatus, additionalData);
      setUserSubscription(result);
      return result;
    } catch (error) {
      throw error;
    }
  };

  // Refresh Subscription wrapper
  const refreshSubscription = async () => {
    if (!user) return;
    const sub = await fetchUserSubscription(user.uid);
    setUserSubscription(sub);
    return sub;
  };

  const value = {
    user,
    userProfile,
    userSubscription,
    loading,
    updateUserProfile,
    updateSubscription,
    refreshSubscription,
    isAuthenticated: !!user,
    // hasSubscription — Business Logic:
    // Returns true if the user has an active paid subscription, a cancelled subscription
    // that still has remaining access (cancel_at_period_end), OR is on the free (basic) plan.
    // Free users (basic) always pass this check regardless of status, because the free tier
    // doesn't require payment activation. Paid plans must be 'active' or 'cancelled' with remaining period.
    hasSubscription: userSubscription && (
      userSubscription.subscriptionStatus === SUBSCRIPTION_CONSTANTS.STATUS.ACTIVE ||
      userSubscription.subscriptionPlan === SUBSCRIPTION_CONSTANTS.PLANS.BASIC ||
      (userSubscription.subscriptionStatus === SUBSCRIPTION_CONSTANTS.STATUS.CANCELLED &&
        userSubscription.currentPeriodEnd &&
        new Date(userSubscription.currentPeriodEnd?.seconds ? userSubscription.currentPeriodEnd.seconds * 1000 : userSubscription.currentPeriodEnd) > new Date())
    )
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
